import { Observable } from "rxjs/Observable"
import { push } from "react-router-redux"
import { Map } from "immutable"

import {
    STUDIO_EDIT_TOPIC_DIMENSION_SEARCH,
    STUDIO_EDIT_TOPICS_LIST_REQUEST,
    STUDIO_EDIT_TOPICS_LIST_COUNT_REQUEST,
    STUDIO_EDIT_DIMENSIONS_LIST_REQUEST,
    STUDIO_EDIT_DIMENSIONS_LIST_COUNT_REQUEST,
    STUDIO_EDIT_RELATED_DIMENSIONS_LIST_REQUEST,
    STUDIO_EDIT_REMOVE_KEY_ISSUE,
    STUDIO_EDIT_CLEAR_KEY_ISSUES,
    STUDIO_EDIT_ADD_KEY_ISSUES_REQUEST,
    STUDIO_EDIT_ADD_KEY_ISSUES_RESPONSE,
    STUDIO_EDIT_CANCEL_MODEL_UPDATE,
    STUDIO_EDIT_APPLY_MODEL_UPDATE,
    STUDIO_ITEM_SAVE_REQUEST,
    STUDIO_ITEM_SAVE_RESPONSE,
    STUDIO_ITEM_RESPONSE,
    STUDIO_ITEM_DELETE_REQUEST,
    STUDIO_ITEM_DELETE_RESPONSE
} from "../actions/action-types"
import {
    studioItemRequest,
    studioItemSaveRequest,
    studioItemSaveResponse,
    studioItemSaveErrorResponse,
    studioItemDeleteResponse,
    studioItemDeleteErrorResponse,
    studioEditTopicsListRequest,
    studioEditTopicsListCountRequest,
    studioEditDimensionsListRequest,
    studioEditDimensionsListCountRequest,
    studioEditUpdateModel,
    studioEditAddKeyIssuesResponse
} from "../actions/studio-actions"
import {
    dimensionListRequest,
    dimensionListCountRequest,
    dimensionItemResponse,
    dimensionItemCachedResponse,
    dimensionItemErrorResponse
} from "../actions/dimension-actions"
import {
    topicListRequest,
    topicListCountRequest
} from "../actions/topic-actions"
import { putMapItem, postMapItem, deleteMapItem } from "../api/map-api"
import { getDimensionItemWithActiveTopics } from "../api/dimension-api"
import {
    normalisingFields as dimensionNormalisingFields,
    hydrate as hydrateDimension
} from "../models/entities/dimension"
import { newMapToAPI } from "../models/entities/map"
import {
    requestItemHandler,
    requestItemSaveEpic,
    requestItemDeleteEpic,
    callbackActionCreatorEpic
} from "./default-epics/default-entity-epics"

export const studioEditApplyModelUpdateEpic = (action$, { getState }) =>
    action$.ofType(STUDIO_EDIT_APPLY_MODEL_UPDATE).map(() => {
        const state = getState()
        const {
            activeId,
            isClonedMap,
            updateModel,
            workingModel,
            imageToUpload,
            imageCopyURL
        } = state.presentation.studio
        const id = isClonedMap ? null : activeId
        const map = !id ? newMapToAPI(workingModel) : updateModel
        let image = undefined
        if (imageToUpload !== undefined) {
            image = imageToUpload
        } else if (isClonedMap && workingModel.image_url) {
            image = workingModel.image_url
        } else {
            // If no image, use "undefined" to indicate that no image uploading should be attempted
            image = imageCopyURL || undefined
        }
        return studioItemSaveRequest(
            id,
            map.toJS(),
            image,
            studioItemSaveResponse
        )
    })

export const requestStudioItemSaveEpic = requestItemSaveEpic(
    STUDIO_ITEM_SAVE_REQUEST,
    putMapItem,
    postMapItem,
    "topic",
    studioItemSaveResponse,
    studioItemSaveErrorResponse
)

export const requestStudioItemDeleteEpic = requestItemDeleteEpic(
    STUDIO_ITEM_DELETE_REQUEST,
    deleteMapItem,
    "topic",
    studioItemDeleteResponse,
    studioItemDeleteErrorResponse
)

export const studioItemDeleteResponseEpic = action$ =>
    action$.ofType(STUDIO_ITEM_DELETE_RESPONSE).map(() => push("/studio"))

export const studioEditTopicDimensionSearchEpic = action$ =>
    action$
        .ofType(STUDIO_EDIT_TOPIC_DIMENSION_SEARCH)
        .debounceTime(500)
        .mergeMap(({ searchTerm }) =>
            Observable.of(
                studioEditTopicsListRequest({
                    searchCriteria: Map({ name: searchTerm })
                }),
                studioEditDimensionsListRequest({
                    searchCriteria: Map({ search: searchTerm })
                }),
                studioEditTopicsListCountRequest({
                    searchCriteria: Map({ name: searchTerm })
                }),
                studioEditDimensionsListCountRequest({
                    searchCriteria: Map({ search: searchTerm })
                })
            )
        )

export const studioEditRelatedDimensionsListRequestEpic = action$ =>
    action$
        .ofType(STUDIO_EDIT_RELATED_DIMENSIONS_LIST_REQUEST)
        .map(({ topicId, callbackActionCreator }) =>
            dimensionListRequest({
                searchCriteria: Map({ statuses: "Active", topics: topicId }),
                callbackActionCreator
            })
        )

const listRequestActionCreatorMapping = {
    STUDIO_EDIT_TOPICS_LIST_REQUEST: topicListRequest,
    STUDIO_EDIT_TOPICS_LIST_COUNT_REQUEST: topicListCountRequest,
    STUDIO_EDIT_DIMENSIONS_LIST_REQUEST: dimensionListRequest,
    STUDIO_EDIT_DIMENSIONS_LIST_COUNT_REQUEST: dimensionListCountRequest
}

export const studioEditListRequestEpic = action$ =>
    action$
        .ofType(
            STUDIO_EDIT_TOPICS_LIST_REQUEST,
            STUDIO_EDIT_TOPICS_LIST_COUNT_REQUEST,
            STUDIO_EDIT_DIMENSIONS_LIST_REQUEST,
            STUDIO_EDIT_DIMENSIONS_LIST_COUNT_REQUEST
        )
        .map(({ type, ...pagingRequest }) =>
            listRequestActionCreatorMapping[type](pagingRequest)
        )

export const studioEditCancelModelUpdateEpic = (action$, { getState }) =>
    action$.ofType(STUDIO_EDIT_CANCEL_MODEL_UPDATE).mergeMap(() => {
        const {
            activeId,
            highlightedId,
            highlightedType
        } = getState().presentation.studio
        const url = generateURL(activeId, highlightedId, highlightedType)
        return Observable.of(studioItemRequest(activeId), push(url))
    })

export const studioItemSaveResponseEpic = (action$, { getState }) =>
    action$.ofType(STUDIO_ITEM_SAVE_RESPONSE).map(() => {
        const {
            activeId,
            highlightedId,
            highlightedType
        } = getState().presentation.studio
        const url = generateURL(activeId, highlightedId, highlightedType)
        return push(url)
    })

function generateURL(activeId, highlightedId, highlightedType) {
    let url = `/studio/${activeId}/view`
    if (highlightedType === "dimension") url += `/${highlightedId}`
    return url + "/feed"
}

export const studioEditRemoveKeyIssueEpic = (action$, { getState }) =>
    action$.ofType(STUDIO_EDIT_REMOVE_KEY_ISSUE).map(({ dimensionId }) => {
        const { workingModel } = getState().presentation.studio
        const dimensions = workingModel.dimensions.filter(
            ({ id }) => id !== dimensionId
        )
        return studioEditUpdateModel("dimensions", dimensions)
    })

export const studioEditClearKeyIssuesEpic = action$ =>
    action$
        .ofType(STUDIO_EDIT_CLEAR_KEY_ISSUES)
        .map(() => studioEditUpdateModel("dimensions", []))

export const studioEditAddKeyIssuesRequestEpic = (action$, { getState }) =>
    action$
        .ofType(STUDIO_EDIT_ADD_KEY_ISSUES_REQUEST)
        .mergeMap(({ dimensionIds, topicId }) => {
            const dimensionItemRequestHandler = requestItemHandler(
                getState,
                getDimensionItemWithActiveTopics,
                "dimensions",
                hydrateDimension,
                dimensionNormalisingFields,
                dimensionItemResponse,
                dimensionItemCachedResponse,
                dimensionItemErrorResponse
            )
            return Observable.forkJoin(
                dimensionIds
                    .map(id => ({
                        id,
                        isInvalidating: false,
                        callbackActionCreator: null
                    }))
                    .map(dimensionItemRequestHandler)
            ).map(dimensionActions =>
                studioEditAddKeyIssuesResponse(dimensionActions, topicId)
            )
        })

export const studioEditAddKeyIssuesResponseEpic = (action$, { getState }) =>
    action$
        .ofType(STUDIO_EDIT_ADD_KEY_ISSUES_RESPONSE)
        .map(({ dimensionActions, ...rest }) => {
            const { dimensions } = getState().presentation.studio.workingModel
            const nonExistingDimensionActions = dimensionActions.filter(
                ({ data: dimension }) =>
                    !dimensions.some(({ id }) => id === dimension.id)
            )
            return { ...rest, dimensionActions: nonExistingDimensionActions }
        })
        .filter(({ dimensionActions }) => dimensionActions.length)
        .map(({ dimensionActions }) => {
            const { dimensions } = getState().presentation.studio.workingModel

            return studioEditUpdateModel(
                "dimensions",
                (dimensions || [])
                    .concat(dimensionActions.map(({ data }) => data))
            )
        })

export const studioCallbackActionCreatorEpic = callbackActionCreatorEpic(
    STUDIO_ITEM_RESPONSE
)
